import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftRight,
  FileText,
  BarChart3,
  GitPullRequestArrow,
  History,
  Server,
  Users
} from "lucide-react";
import { Sidebar, SidebarContent, SidebarHeader, SidebarFooter, SidebarMenu, SidebarMenuItem, SidebarMenuButton } from "./ui/sidebar";
import { useAuthStore } from "../store/useAuthStore";
import { useStreemLogout, useMigrationCapabilities } from "../lib/api";
import { useStartMigration } from "../hooks/useStartMigration";
import { NavMain, type NavItem } from "./nav-main";
import { NavUser } from "./nav-user";
import { ThemeToggle } from "./theme-toggle";

interface AppSidebarProps {
  variant?: "sidebar" | "floating" | "inset";
}

export function AppSidebar({ variant = "sidebar" }: AppSidebarProps) {
  const navigate = useNavigate();
  const { user, isDefaultUser, allowedModules, canMigrateProcess, canMigrateOntology } = useAuthStore();
  const { mutateAsync: logout } = useStreemLogout();
  const { startOntology, startProcess, startActionEffect } = useStartMigration();
  // Populate migration capabilities into the store (deduped by react-query across components).
  useMigrationCapabilities();

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
      // Even if logout fails, clear local state and redirect
      useAuthStore.getState().logout();
      window.location.href = "/";
    }
  };

  // Handle navigation
  const handleNavigate = (url: string) => {
    if (url.startsWith('/')) {
      navigate(url);
    }
  };

  // Get current path
  const currentPath = window.location.pathname;

  // Main navigation items — filtered by allowedModules for default users.
  // Workflows is a grouped item with two sub-categories matching the WorkflowPage layout.
  const allNavItems: Array<NavItem & { module: string }> = [
    // Non-admin users pick their source→target pair before any workflow; the admin has no pair.
    ...(!isDefaultUser
      ? [{
          title: "Select Instances",
          url: "/select-instances",
          icon: ArrowLeftRight,
          module: "WORKFLOWS",
          isActive: currentPath === "/select-instances",
        }]
      : []),
    {
      // The DEFAULT admin is management-only: its capabilities are always false, so the migration
      // sub-groups below come out empty and /workflows renders the Administration landing.
      title: isDefaultUser ? "Home" : "Workflows",
      url: "/workflows",
      icon: BarChart3,
      module: "WORKFLOWS",
      isActive: currentPath === "/workflows" || currentPath.includes("/workflow"),
      // Sub-items are gated by the operator's migration capabilities (resolved from their DWI
      // role). Empty groups are filtered out below.
      groups: [
        {
          label: "Checklist-based",
          items: [
            // Ontology Migration → ontology permission.
            ...(canMigrateOntology
              ? [{ title: "Ontology Migration", onClick: startOntology }]
              : []),
            // Process + Linked Process → process permission.
            ...(canMigrateProcess
              ? [
                  // Linked-process migration is merged into Process Migration: the wizard detects
                  // linked children after the target step and inserts a "Resolve links" step.
                  { title: "Process Migration", onClick: startProcess },
                ]
              : []),
          ],
        },
        {
          label: "Ontology-based",
          items: canMigrateOntology
            ? [
                allowedModules.includes("ACTION_EFFECT_MIGRATION")
                  ? { title: "Action Effect Migration", onClick: startActionEffect }
                  : { title: "Action Effect Migration", disabled: true, badge: "Soon" },
              ]
            : [],
        },
      ].filter((group) => group.items.length > 0),
    },
    {
      title: "Logs",
      url: "/logs",
      icon: FileText,
      module: "LOGS",
      isActive: currentPath === "/logs",
    },
  ];

  const mainNavItems: NavItem[] = isDefaultUser
    ? allNavItems.filter((item) => allowedModules.includes(item.module))
    : allNavItems;

  // No document items or secondary navigation items needed

  // User data
  const userData = {
    name: user ? `${user.firstName} ${user.lastName}` : "User",
    email: user?.email || "user@example.com",
    avatar: "" // We don't have avatar in the User type, so we'll use an empty string
  };

  return (
    <Sidebar collapsible="icon" variant={variant}>
      <SidebarHeader className="border-b">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <GitPullRequestArrow className="!size-5" />
              <span className="text-base font-semibold">Migration Utility</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {/* Main navigation */}
        <NavMain 
          items={mainNavItems} 
          onNavigate={handleNavigate}
        />
      </SidebarContent>
      <SidebarFooter className="border-t">
        {/* Theme toggle */}
        <ThemeToggle />
        {/* Admin: user + instance management (DEFAULT/admin only, matching the server-side gates
            on /api/admin/users and /api/connected-services) */}
        {isDefaultUser && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => handleNavigate("/users")}
                isActive={currentPath === "/users"}
              >
                <Users />
                <span>Users</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => handleNavigate("/connected-services")}
                isActive={currentPath === "/connected-services"}
              >
                <Server />
                <span>Connected Services</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        {/* Session activity — every user sees everyone's events (org-visible log). */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => handleNavigate("/session-activity")}
              isActive={currentPath === "/session-activity"}
            >
              <History />
              <span>Session Activity</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {/* User profile */}
        <NavUser
          user={userData}
          onLogout={handleLogout}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
